import React, { useEffect, useRef, useState } from "react";
import { notification } from "antd";
import { CircularProgress } from "@mui/material";
import { useDispatch } from "react-redux";
import { getAllHighRisksData, getAllSwmsDropdowns, getSwmsFormData } from "../../../../../../actions/v2/JobSwmsActions";
import { Strings } from "../../../../../../dataProvider/localize";
import JobSwmsFormV3Shell from "./JobSwmsFormV3Shell.jsx";
import { serializeInitialValuesV3 } from "./SwmsFormV3Serializer";

const reorderSwmsDataSections = (swmsData) => {
  if (!swmsData || !swmsData.sections || typeof swmsData.sections !== "object") {
    return swmsData;
  }

  const sections = swmsData.sections;
  const metaSequence = swmsData.meta && swmsData.meta.sequence ? swmsData.meta.sequence : {};
  const fixedArray = Array.isArray(metaSequence.fixed) ? metaSequence.fixed : [];
  const activeArray = Array.isArray(metaSequence.active) ? metaSequence.active : [];

  if (fixedArray.length === 0 && activeArray.length === 0) {
    return swmsData;
  }

  const seen = new Set();
  const orderedSectionEntries = [];

  // Helper to sort sequence items by sequence_order
  const sortBySequenceOrder = (items) => {
    return [...items].sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));
  };

  // Process fixed sections first (sorted by sequence_order)
  const sortedFixed = sortBySequenceOrder(fixedArray);
  sortedFixed.forEach((seqItem) => {
    const itemKey = seqItem.item_key;
    if (!seen.has(itemKey) && Object.prototype.hasOwnProperty.call(sections, itemKey)) {
      seen.add(itemKey);
      orderedSectionEntries.push([itemKey, sections[itemKey]]);
    }
  });

  // Process active sections second (sorted by sequence_order)
  const sortedActive = sortBySequenceOrder(activeArray);
  sortedActive.forEach((seqItem) => {
    const itemKey = seqItem.item_key;
    if (!seen.has(itemKey) && Object.prototype.hasOwnProperty.call(sections, itemKey)) {
      seen.add(itemKey);
      orderedSectionEntries.push([itemKey, sections[itemKey]]);
    }
  });

  // Append any remaining sections not in fixed or active
  Object.keys(sections).forEach((key) => {
    if (!seen.has(key)) {
      seen.add(key);
      orderedSectionEntries.push([key, sections[key]]);
    }
  });
  return {
    ...swmsData,
    sections: Object.fromEntries(orderedSectionEntries)
  };
};

export default function JobSwmsFormV3Container({ onClose, swms, jobId }) {
  const dispatch = useDispatch();
  const controller = useRef();

  const [loadingData, setLoadingData] = useState(false);
  const [formData, setFormData] = useState(null);

  const resolvedJobId = jobId 
    || (swms && swms.version && swms.version.job_id)
    || (swms && swms.task && (swms.task.job_id || swms.task.jobId));
  const resolvedSwmsId = (swms && swms.version && swms.version.swms_form_id) || (swms && swms.swms_form_id);

  const loadFormData = () => {
    setLoadingData(true);
    controller.current = new AbortController();

    const promises = [
      dispatch(getAllSwmsDropdowns({ signal: controller.current.signal })),
      dispatch(getSwmsFormData({ signal: controller.current.signal }, resolvedSwmsId, resolvedJobId)),
      dispatch(getAllHighRisksData({ signal: controller.current.signal }))
    ];

    Promise.all(promises)
      .then((data) => {
        const apiFormData = data[1] || null;
        const normalizedSwmsData = reorderSwmsDataSections(apiFormData && apiFormData.swms_data ? apiFormData.swms_data : null);

        setFormData(apiFormData ? { ...apiFormData, swms_data: normalizedSwmsData } : null);
        setLoadingData(false);
      })
      .catch((err) => {
        setLoadingData(false);
        if (err) {
          notification.error({
            message: Strings.error_title,
            description: err ? err : Strings.generic_error,
            className: "ant-error"
          });
        }
        onClose();
      });
  };

  useEffect(() => {
    loadFormData();

    return () => {
      if (controller.current) {
        controller.current.abort();
      }
    };
  }, []);

  if (loadingData) {
    return (
      <div className="col-12 d-flex align-items-center justify-content-center p-5">
        <CircularProgress size={20} />
      </div>
    );
  }

  return (
    <JobSwmsFormV3Shell
      onClose={onClose}
      swmsData={formData && formData.swms_data ? formData.swms_data : null}
      jobId={resolvedJobId}
      approvalSubmissionHistory={formData && formData.approvalSubmissionHistory ? formData.approvalSubmissionHistory : []}
      swmsTitle={formData && formData.title ? formData.title : ""}
      swmsNumber={formData && formData.swmsNumber ? formData.swmsNumber : ""}
      swmsId={resolvedSwmsId}
      initialValues={serializeInitialValuesV3(formData && formData.swms_data ? formData.swms_data : {})}
    />
  );
}
